/**
 * Inspect mode: tile-level debugging. Toggle with the tilde/backquote key;
 * click placements to select them (raycast, drag-suppressed so orbiting
 * still works); copy a portable JSON report of
 * "these tiles on this seed on this version".
 */
import * as THREE from "three";
import type { SceneCtx } from "./scene.ts";
import type { Placement, Track } from "../shared/types.ts";
import { genVersion } from "../gen/version.ts";

export interface InspectSource {
  seed: string;
  track: Track | null;
  /** Scene group whose children carry userData.placementIndex. */
  group: THREE.Group | null;
}

export interface Inspector {
  /** Drop all selections/highlights (called on regenerate). */
  reset(): void;
  readonly active: boolean;
  /** Selected placement indices, for CUA state dumps. */
  selection(): number[];
}

export function installInspect(
  ctx: SceneCtx,
  source: () => InspectSource,
): Inspector {
  let active = false;
  const selected = new Map<number, THREE.Box3Helper>();

  // --- DOM ------------------------------------------------------------------
  const panel = document.createElement("div");
  panel.id = "inspect";
  panel.style.cssText =
    "position:fixed;top:12px;right:12px;z-index:20;display:none;" +
    "flex-direction:column;gap:8px;align-items:flex-end;" +
    "font:12px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace;color:#dce6f5;";
  const buttons = document.createElement("div");
  buttons.style.cssText = "display:flex;gap:8px;";
  const mkBtn = (label: string, onClick: () => void): HTMLButtonElement => {
    const b = document.createElement("button");
    b.textContent = label;
    b.style.cssText =
      "background:rgba(14,20,32,.72);color:#dce6f5;cursor:pointer;" +
      "border:1px solid rgba(220,230,245,.25);border-radius:6px;" +
      "padding:6px 10px;font:inherit;backdrop-filter:blur(6px);";
    b.addEventListener("click", onClick);
    buttons.appendChild(b);
    return b;
  };
  const list = document.createElement("div");
  list.style.cssText =
    "background:rgba(14,20,32,.72);border:1px solid rgba(220,230,245,.25);" +
    "border-radius:6px;padding:8px 10px;white-space:pre;text-align:left;" +
    "max-height:50vh;overflow:auto;backdrop-filter:blur(6px);";
  const copyBtn = mkBtn("copy debug info", () => copyReport());
  mkBtn("clear selection", () => clearSelection());
  mkBtn("exit mode", () => setActive(false));
  panel.appendChild(buttons);
  panel.appendChild(list);
  document.body.appendChild(panel);

  function describe(index: number, p: Placement): string {
    const cell = `${Math.round(p.pos[0])},${Math.round(p.pos[2])}`;
    return `#${index} ${p.pieceId} @ (${cell}) y${p.pos[1]} r${p.rotation}` +
      `${p.mirrored ? " mirrored" : ""} [${p.role}]`;
  }

  function renderList(): void {
    const { seed } = source();
    const lines = [...selected.keys()].sort((a, b) => a - b).map((i) => {
      const p = source().track?.placements[i];
      return p ? describe(i, p) : `#${i} ?`;
    });
    list.textContent = `inspect • ${seed} • ${genVersion()}\n` +
      (lines.length ? lines.join("\n") : "click tiles to select");
  }

  function report(): string {
    const { seed, track } = source();
    return JSON.stringify(
      {
        seed,
        version: genVersion(),
        url: location.href,
        tiles: [...selected.keys()].sort((a, b) => a - b).map((i) => {
          const p = track?.placements[i];
          return p
            ? {
              i,
              piece: p.pieceId,
              cell: [Math.round(p.pos[0]), Math.round(p.pos[2])],
              pos: p.pos.map((v) => Number(v.toFixed(2))),
              rot: p.rotation,
              ...(p.mirrored ? { mirrored: true } : {}),
              role: p.role,
            }
            : { i };
        }),
      },
      null,
      1,
    );
  }

  function copyReport(): void {
    const text = report();
    console.log("[inspect]", text);
    const showInline = () => {
      // Clipboard unavailable — render the JSON in the panel (user-select:
      // all) so it can be copied by hand.
      list.textContent = text;
      (list.style as CSSStyleDeclaration).userSelect = "all";
      copyBtn.textContent = "select & copy below";
      setTimeout(() => (copyBtn.textContent = "copy debug info"), 2000);
    };
    if (!navigator.clipboard) {
      showInline();
      return;
    }
    navigator.clipboard.writeText(text).then(() => {
      copyBtn.textContent = "copied!";
      setTimeout(() => (copyBtn.textContent = "copy debug info"), 1200);
    }).catch(showInline);
  }

  // --- selection ------------------------------------------------------------
  function drop(index: number, helper: THREE.Box3Helper): void {
    ctx.scene.remove(helper);
    helper.geometry.dispose();
    (helper.material as THREE.Material).dispose();
    selected.delete(index);
  }

  function clearSelection(): void {
    for (const [i, helper] of [...selected]) drop(i, helper);
    renderList();
  }

  function toggle(index: number, obj: THREE.Object3D): void {
    const cur = selected.get(index);
    if (cur) {
      drop(index, cur);
    } else {
      const box = new THREE.Box3().setFromObject(obj);
      const helper = new THREE.Box3Helper(box, new THREE.Color(0xff4757));
      helper.name = `inspect:${index}`;
      ctx.scene.add(helper);
      selected.set(index, helper);
    }
    renderList();
  }

  // --- picking (drag-suppressed click) ---------------------------------------
  const ray = new THREE.Raycaster();
  const ndc = new THREE.Vector2();
  let downAt: [number, number] | null = null;

  ctx.renderer.domElement.addEventListener("pointerdown", (e: PointerEvent) => {
    if (active) downAt = [e.clientX, e.clientY];
  });
  ctx.renderer.domElement.addEventListener("pointerup", (e: PointerEvent) => {
    if (!active || !downAt) return;
    const moved = Math.hypot(e.clientX - downAt[0], e.clientY - downAt[1]);
    downAt = null;
    if (moved > 5) return; // orbit drag, not a pick

    const rect = ctx.renderer.domElement.getBoundingClientRect();
    ndc.set(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    ray.setFromCamera(ndc, ctx.camera);
    const { group } = source();
    if (!group) return;
    for (const hit of ray.intersectObject(group, true)) {
      let node: THREE.Object3D | null = hit.object;
      if (node.name === "ground") continue;
      while (node && node.userData.placementIndex === undefined) {
        node = node.parent;
      }
      if (node) {
        toggle(node.userData.placementIndex as number, node);
        return;
      }
    }
  });

  // --- mode toggle ------------------------------------------------------------
  function setActive(on: boolean): void {
    active = on;
    panel.style.display = on ? "flex" : "none";
    if (on) renderList();
  }

  addEventListener("keydown", (e) => {
    if (e.code === "Backquote") setActive(!active);
  });

  return {
    reset() {
      clearSelection();
      renderList();
    },
    get active() {
      return active;
    },
    selection() {
      return [...selected.keys()].sort((a, b) => a - b);
    },
  };
}
